/**
 * Letter-of-Instruction question parsing for the QtA (Question To Answer)
 * module. Restored from the pre-MDRS-8 utility — the old SectionFields UI
 * used this to render a card-per-question with paired textareas; we now
 * use it to seed paragraphs in the docx editor.
 *
 * The QtA module's `summaries_from_doc_sorter` is a raw multi-line string
 * produced by the additional-comp-extractor-func /extract-loi-questions
 * endpoint, e.g.:
 *
 *     Attachment 1
 *     1.0 Please describe the worker's condition...
 *     1.1 Include any sub-points
 *     2.0 In your opinion is the condition causally related...
 *
 * A question starts on a line beginning with a DOTTED number — `1.`, `1.0`,
 * or `1.1` — and sub-questions (`n.m`) are questions in their own right, not
 * continuation text: the LOI numbers them separately, so the expert needs a
 * separate paragraph to answer each (MDRS-265). Subsequent non-empty lines
 * without that prefix fold into the prior question as continuation text.
 * Leading "Attachment 1" lines are dropped.
 *
 * The dot is required. A bare leading number (`15 March 2024 ...`,
 * `3 physiotherapy reports ...`) is prose, not a question marker — accepting
 * it fabricated questions out of dates and counts, and split real questions
 * mid-sentence when a wrapped line happened to start with a number.
 */

/** One question parsed from the raw LOI text. */
export type LoiQuestion = string;

/** Parse the raw LOI questions text into one entry per main question.
 *  Continuation lines (sub-questions, wrapped paragraphs) are folded into
 *  the preceding main question separated by spaces. */
export const parseLoiQuestions = (rawText: string): LoiQuestion[] => {
  if (!rawText) return [];

  const cleaned = rawText
    .split("\n")
    .filter((line) => !/^\s*attachment\s*1\b/i.test(line.trim()))
    .join("\n");

  const lines = cleaned.split("\n");
  // Question start: digits, a REQUIRED dot, then an optional sub-number, then
  // whitespace. Matches `1.`, `1.0` and `1.1`; deliberately rejects a bare
  // `15 ` so dates/counts in wrapped prose stay continuation text.
  const questionRegex = /^(\d+)\.(\d+)?\s+(.*)/;

  const sections: { num: string; content: string }[] = [];
  let current: { num: string; content: string } | null = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const match = trimmed.match(questionRegex);
    if (match) {
      if (current) sections.push(current);
      current = { num: `${match[1]}.${match[2] ?? "0"}`, content: trimmed };
    } else if (current) {
      current.content += "\n" + trimmed;
    }
  }
  if (current) sections.push(current);

  return sections
    .map((s) => s.content.trim().replace(/\n+/g, " ").trim())
    .filter((q) => q.length > 0);
};

/** Pair each parsed question with the expert's saved answer (if any).
 *  The old UI persisted answers as `experts_response` joined by `\n\n` —
 *  index N maps to question N. Excess answer paragraphs are dropped;
 *  missing answers become empty strings. */
export const stitchAnswers = (
  questions: LoiQuestion[],
  expertsResponse: string | null | undefined
): Array<{ question: LoiQuestion; answer: string }> => {
  const raw = expertsResponse?.trim() ?? "";
  const answers = raw ? raw.split(/\n{2,}/).map((a) => a.trim()) : [];
  return questions.map((q, i) => ({ question: q, answer: answers[i] ?? "" }));
};
